import { atom } from 'jotai'

/** { purchaseType: { flavorKey: qty } }
 *  e.g. { subscribe: { flavor_0: 1 } }
 */
export type CartItems = Record<string, Record<string, number>>

export type FormatTerms = {
    subtotal: number
    total: number
    pv: number
    currency: string
    tax: { amount: number; percentage: number }
    freight: { amount: number }
}

export const cartAtom = atom<CartItems>({})
export const cartUuidAtom = atom<string>('')
export const isCartLoadingAtom = atom<boolean>(false)
export const formatTermsAtom = atom<FormatTerms | null>(null)

/**
 * Flips to `true` the first time the user mutates the cart in the current
 * session (add / remove / change qty). Consumers that render the order
 * summary use this to decide whether an empty `cartAtom` means
 *   (a) "the user just cleared the cart" — show empty, cart is authoritative,
 *   (b) "cart hasn't been hydrated yet on this page" — fall back to the
 *       server-provided item list.
 * Without this signal the summary renders stale `mapped.items` after the
 * last item is removed and debounce/PUT/GET roundtrip hasn't finished.
 */
export const cartEditedAtom = atom<boolean>(false)

export type V2CartItem = {
    product_id: string
    selection: Record<string, string>
    purchase_option: string
    qty: number
}

/** V2 cart — flat item list (product_id + selection + qty). Country-agnostic
 *  cart state; the active country plugin reads/writes it. */
export const v2CartAtom = atom<V2CartItem[]>([])

/** Total number of items in cart */
export const cartTotalItemsAtom = atom<number>(get => {
    const cart = get(cartAtom)
    return Object.values(cart).reduce((total, flavors) => total + Object.values(flavors).reduce((t, qty) => t + qty, 0), 0)
})

/** First item in cart for quick display */
export const cartFirstItemAtom = atom<{
    purchaseType: string
    flavor: string
    qty: number
} | null>(get => {
    const cart = get(cartAtom)
    for (const [purchaseType, flavors] of Object.entries(cart)) {
        for (const [flavor, qty] of Object.entries(flavors)) {
            if (qty > 0) return { purchaseType, flavor, qty }
        }
    }
    return null
})
